/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox のチケット一括取込の状態機械。モーダルの開閉・照会中フラグ・見つからなかった案件番号を保持し、照会 API の結果を呼び出し元へ渡す
 * @graph-connects useTicketLookup [calls] 案件番号から案件メタ情報を一括照会
 */
import { useCallback, useState } from 'react';
import { useTicketLookup } from '../../../../hooks/useProjects.js';
import type { TicketLookupItem } from '../../../../types/project.js';

/** チケット一括取込の状態と操作。 */
export interface TicketImportController {
  /** モーダルを開いているか。 */
  open: boolean;
  /** 照会 API 待ちか。 */
  importing: boolean;
  /** 直近の照会が失敗したか。 */
  failed: boolean;
  /** 直近の照会で引けなかった案件番号。 */
  notFound: string[];
  /** モーダルを開く（前回の結果はクリアする）。 */
  openModal: () => void;
  /** モーダルを閉じる。 */
  closeModal: () => void;
  /** 案件番号のトークン列を照会して取り込む。 */
  importTickets: (tokens: string[]) => void;
}

/**
 * チケット一括取込のフック。引けた案件は `onImported` へ渡し、ブロック生成は呼び出し元に任せる。
 *
 * 1 件も引けなかったときはモーダルを開いたままにする。閉じてしまうと、なぜ 0 件だったのか
 * （どの案件番号が引けなかったのか）をユーザーが確認できなくなるため。
 *
 * @graph-connects none
 */
export function useTicketImport(
  onImported: (items: TicketLookupItem[]) => void,
): TicketImportController {
  const [open, setOpen] = useState(false);
  const [notFound, setNotFound] = useState<string[]>([]);
  const lookup = useTicketLookup();

  const openModal = useCallback(() => {
    setNotFound([]);
    lookup.reset();
    setOpen(true);
  }, [lookup]);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const importTickets = useCallback(
    (tokens: string[]) => {
      lookup.mutate(tokens, {
        onSuccess: (res) => {
          setNotFound(res.notFound);
          onImported(res.items);
          if (res.items.length > 0) setOpen(false);
        },
      });
    },
    [lookup, onImported],
  );

  return {
    open,
    importing: lookup.isPending,
    failed: lookup.error !== null,
    notFound,
    openModal,
    closeModal,
    importTickets,
  };
}
